/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import personFields from "../modules/personFields";
import * as stagesCase from "../../../Stages/StagesCase";
import {
  dateFieldDefault,
  instituteReferenceDefault,
  invoiceNetValueNotOverpaid,
  percentageFieldDefault
} from "../../../validationRules";
import _ from "lodash";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import DateChangeButtonsToolbar from "../../../../components/Validator/DateChangeButtonsToolbar";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import getExtrajudicialRecommendationOptions from "./extrajudicialRecommendationOptions";
import { getLitigationCaseRejectionCategorySelectOptions } from "./litigationCaseFormHelperService";
import { LitigationUnion } from "../../../../types/LitigationUnion";
import { courtPostalCodeAutoCompleteAdditionalProps } from "../../../formFieldsDefaultDefinitions";

export const abstractLitigationStageFormDefinition = (
  product: LitigationUnion,
): ReturnType<FormDefinition<LitigationUnion>> => {
  const createdFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
  ];

  const dataCompletedFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.legalExpenseInsurance`,
      options: () => trueFalseDefaultOptions("trafficAccident.legalExpenseInsurance"),
      isHidden: ({ product }) => Boolean(product.caseInsurance.iIWPI),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfClaimFiledDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.advisoryHandling === true || product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isHidden: ({ product }) =>
        ["paymentOrder", "enforcementOrder", "lawsuit"].includes(product.defenseProcessStatus) &&
        product.caseCategory === "defendClaim",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isHidden: ({ product }) =>
        !(
          ["paymentOrder", "enforcementOrder", "lawsuit"].includes(product.defenseProcessStatus) &&
          product.caseCategory === "defendClaim"
        ),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.advisoryHandling === true || product.isLawsuitHandling || product.caseCategory === "defendClaim",
      additionalProps: {
        toolbar: DateChangeButtonsToolbar,
      },
    },
    {
      type: "ValidatorAutoComplete",
      path: `${product.productClassName}.courtPostalCode`,
      additionalProps: courtPostalCodeAutoCompleteAdditionalProps,
      helperText:
        "Bitte hier die Postleitzahl eintragen, die für den Gerichtsstand ermittelt wurde und NICHT die Postleitzahl des Gerichtes.",
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" ||
        !product.isLawsuitHandling ||
        product.caseCategory === "defendClaim" ||
        product.isHigherInstanceCase,
      alert: {
        onChange:
          "Das Ändern der Postleitzahl führt dazu, dass das zuständige Gericht erneut ermittelt wird. Möchten Sie die Postleitzahl tatsächlich ändern?",
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategoryAssessment`,
      isDisabled: ({ product }) => (product.stage || 0) > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
      isHidden: ({ product }) => product.caseCategory === "advisory",
      options: () => [{ value: "confirm" }, { value: "reject" }],
    },
    {
      type: "CaseRejectionCategorySelect",
      path: `${product.productClassName}.caseRejectionCategory`,
      options: () => getLitigationCaseRejectionCategorySelectOptions(product),
      isDisabled: ({ product }) => (product.stage || 0) > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
      isHidden: ({ product, values }) =>
        product.caseCategory === "advisory" || values[product.productClassName].caseCategoryAssessment !== "reject",
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.assessmentRejectionText`,
      isHidden: ({ product, values }) =>
        product.caseCategory === "advisory" || values[product.productClassName].caseCategoryAssessment !== "reject",
      additionalProps: {
        multiline: true,
        rows: 12,
      },
    },
  ];

  const dataLawFirmCompletedFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [...dataCompletedFields];

  const checkedFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.legalExpenseInsurance`,
      options: () => trueFalseDefaultOptions("trafficAccident.legalExpenseInsurance"),
      isHidden: ({ product }) => Boolean(product.caseInsurance.iIWPI),
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategoryAssessment`,
      isDisabled: ({ product }) => (product.stage || 0) > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
      isHidden: ({ product }) => product.caseCategory === "advisory",
      options: () => [{ value: "confirm" }, { value: "reject" }],
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.assessmentRejectionText`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment !== "reject" || product.caseCategory === "advisory",
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationSent`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.advisoryHandling === true || product.isLawsuitHandling,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.advisoryHandling === true || product.isLawsuitHandling || product.caseCategory === "defendClaim",
      additionalProps: {
        toolbar: DateChangeButtonsToolbar,
      },
    },
  ];

  const inProgressFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategoryAssessment`,
      isDisabled: ({ product }) =>
        (product.stage || 0) > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
      isHidden: ({ product }) =>
        product.advisoryHandling === true || !product.additionalClaimText,
      options: () => [{ value: "confirm" }, { value: "reject" }],
    },
  ];

  const communicationSentFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationSent`,
      isHidden: ({ product }) => product.advisoryHandling === true,
      isDisabled: () => true,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.advisoryHandling === true || product.isLawsuitHandling || product.caseCategory === "defendClaim",
      additionalProps: {
        toolbar: DateChangeButtonsToolbar,
      },
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialReactionReceived`,
      validators: dateFieldDefault,
      isHidden: ({ product, values }) =>
        values[product.productClassName].extrajudicialReaction === "noReaction",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.extrajudicialReaction`,
      translationPath: `${product.productClassName}.extrajudicialReaction.${product.caseCategory}`,
      options: () => [
        { value: "noReaction" },
        { value: "fullyAgreed" },
        { value: "partiallyAgreed" },
        { value: "disagreed" },
      ],
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.extrajudicialRecommendation`,
      isHidden: ({ product }) =>
        !_.includes(["defendClaim", "enforceClaim"], product.caseCategory),
      options: ({ product }) => getExtrajudicialRecommendationOptions(product),
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.extrajudicialRecommendationText`,
      isHidden: ({ product }) =>
        !_.includes(["defendClaim", "enforceClaim"], product.caseCategory),
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
  ];

  const reactionReceivedFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationSent`,
      isHidden: ({ product }) => product.advisoryHandling === true,
      isDisabled: () => true,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.extrajudicialCommunicationDeadline`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.advisoryHandling === true || product.isLawsuitHandling || product.caseCategory === "defendClaim",
      additionalProps: {
        toolbar: DateChangeButtonsToolbar,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.extrajudicialReaction`,
      translationPath: `${product.productClassName}.extrajudicialReaction.${product.caseCategory}`,
      isDisabled: () => true,
      options: () => [
        { value: "noReaction" },
        { value: "fullyAgreed" },
        { value: "partiallyAgreed" },
        { value: "disagreed" },
      ],
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.extrajudicialRecommendation`,
      isDisabled: () => true,
      isHidden: ({ product }) => !_.includes(["defendClaim", "enforceClaim"], product.caseCategory),
      options: ({ product }) => getExtrajudicialRecommendationOptions(product),
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.extrajudicialRecommendationText`,
      isDisabled: () => true,
      isHidden: ({ product }) =>
        !_.includes(["defendClaim", "enforceClaim"], product.caseCategory) || !product.extrajudicialRecommendationText,
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.finalClaimStatusClient`,
      translationPath: `${product.productClassName}.finalClaimStatusClient.${product.caseCategory}`,
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
      options: () => [
        { value: "fullyAgreed" },
        { value: "partiallyAgreed" },
        { value: "notAgreed" },
        { value: "noReaction" },
      ],
      isHidden: ({ product }) => product.productClassName === "trafficAccident",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.finalProcessFeedbackClient`, // Rückmeldung Mandant
      translationPath: `${product.productClassName}.finalProcessFeedbackClient.${product.caseCategory}`, // Rückmeldung Mandant
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
      isHidden: ({ product, values }) => values[product.productClassName].finalClaimStatusClient === "fullyAgreed",
      options: () => [{ value: "agreement" }, { value: "lawsuit" }],
    },
    {
      type: "ValidatorAutoComplete",
      path: `${product.productClassName}.courtPostalCode`,
      additionalProps: courtPostalCodeAutoCompleteAdditionalProps,
      helperText:
        "Bitte hier die Postleitzahl eintragen, die für den Gerichtsstand ermittelt wurde und NICHT die Postleitzahl des Gerichtes.",
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" ||
        !product.isLawsuitHandling ||
        product.caseCategory === "defendClaim" ||
        product.isHigherInstanceCase,
      alert: {
        onChange:
          "Das Ändern der Postleitzahl führt dazu, dass das zuständige Gericht erneut ermittelt wird. Möchten Sie die Postleitzahl tatsächlich ändern?",
      },
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isDisabled: () => true,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialFinal`,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentExtrajudicial`, // required for extrajudicial invoice when we go directly to stage "completed"
      fieldType: "money",
      numberType: "float",
    },
  ];

  const actionRequiredFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.caseInsurance.extrajudicialCoverageRequestAnsweredDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.paymentType !== "insurance" ||
        (product.productCategory === "administrativeLaw" && product.administrativeCaseType !== "main"),
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseInsurance.extrajudicialCoverageGranted`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.extrajudicialCoverageGranted`),
      isHidden: ({ product }) =>
        product.paymentType !== "insurance" ||
        (product.productCategory === "administrativeLaw" && product.administrativeCaseType !== "main"),
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isDisabled: () => true,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.subtractExtrajudicialProcessFee`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.subtractExtrajudicialProcessFee`),
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeExtrajudicialFinal`, // required for extrajudicial invoice that is generated on "prepare lawsuit stage"
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentExtrajudicial`, // required for extrajudicial invoice that is generated on "prepare lawsuit stage"
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialPreliminary`, // required for statement of claim that is generated on "prepare lawsuit stage"
      fieldType: "money",
      numberType: "float",
    },
  ];

  const prepareLawsuitFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.caseInsurance.judicialCoverageRequestAnsweredDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.paymentType !== "insurance",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseInsurance.judicialCoverageGranted`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.extrajudicialCoverageGranted`),
      isHidden: ({ product }) => product.paymentType !== "insurance",
    },
    {
      type: "ValidatorAutoComplete",
      path: `${product.productClassName}.courtPostalCode`,
      additionalProps: courtPostalCodeAutoCompleteAdditionalProps,
      helperText:
        "Bitte hier die Postleitzahl eintragen, die für den Gerichtsstand ermittelt wurde und NICHT die Postleitzahl des Gerichtes.",
      isHidden: ({ product }) =>
        product.caseCategory === "defendClaim" ||
        product.isHigherInstanceCase ||
        (product.productCategory === "administrativeLaw" && product.administrativeCaseType === "main"),
      alert: {
        onChange:
          "Das Ändern der Postleitzahl führt dazu, dass das zuständige Gericht erneut ermittelt wird. Möchten Sie die Postleitzahl tatsächlich ändern?",
      },
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.subtractExtrajudicialProcessFee`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.subtractExtrajudicialProcessFee`),
      isHidden: ({ product }) =>
        product.productCategory === "administrativeLaw" && product.administrativeCaseType !== "main",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfClaimSentDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "enforceClaim",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfClaimFiledDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
  ];

  const evaluateJudgementFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.nextInstanceRecommendation`,
      options: () => [{ value: "appeal" }, { value: "noAppeal" }],
      alert: {
        onChange:
          "Mit der Auswahl einer Option wird sofort und automatisch eine Anfrage an die Mandantschaft gesendet. Daher bitte sicherstellen, dass die Empfehlung final ist.",
        onInitial:
          "Mit der Auswahl einer Option wird sofort und automatisch eine Anfrage an die Mandantschaft gesendet. Daher bitte sicherstellen, dass die Empfehlung final ist.",
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.nextInstanceDecisionClient`,
      helperText:
        "Achtung: Feld ist über Servicewelt durch Mandantschaft zu befüllen! Wenn dieses Feld von uns befüllt wird, " +
        "kann die Mandantschaft kein Feedback mehr abgeben. Daher nur befüllen, wenn mit Mandantschaft explizit abgesprochen.",
      alert: {
        onInitial:
          "Sie möchten den Wert im Feld “Entscheidung Mandantschaft nächste Instanz” erstmalig ändern. Dieses " +
          "Feld wird standardmäßig durch die Mandantschaft über eine Anfrage in der Servicewelt befüllt und sollte " +
          "nur im Ausnahmefall und nach ausdrücklicher Abstimmung mit der Mandantschaft manuell befüllt werden! " +
          "Mit der Auswahl einer Option und anschließendem Speichern wird der Fall in die Stage Rechtsstreit beendet " +
          "verschoben. Sofern Sie “Berufung einlegen” auswählen, wird ein neuer Fall angelegt und darin ggf. eine " +
          "Deckungsanfrage an die Versicherung gesendet. Daher bitte sicherstellen, dass die Entscheidung final ist.",
        onChange:
          "Sie möchten den Wert im Feld “Entscheidung Mandantschaft nächste Instanz” erstmalig ändern. Dieses " +
          "Feld wird standardmäßig durch die Mandantschaft über eine Anfrage in der Servicewelt befüllt und sollte " +
          "nur im Ausnahmefall und nach ausdrücklicher Abstimmung mit der Mandantschaft manuell befüllt werden! " +
          "Mit der Auswahl einer Option und anschließendem Speichern wird der Fall in die Stage Rechtsstreit beendet " +
          "verschoben. Sofern Sie “Berufung einlegen” auswählen, wird ein neuer Fall angelegt und darin ggf. eine " +
          "Deckungsanfrage an die Versicherung gesendet. Daher bitte sicherstellen, dass die Entscheidung final ist.",
      },
      isDisabled: ({ product }) =>
        !_.includes([stagesCase.STAGE_LAWSUIT_COMPLETED, stagesCase.STAGE_EVALUATE_JUDGEMENT], product.stage),
      options: () => [{ value: "appeal" }, { value: "checkJudgement" }, { value: "noAppeal" }],
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.promulgationOfJudgementDate`,
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.judgementEebSentDate`,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.judgementReceivedDate`,
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.costBearingByOpponentRelative`,
      validators: percentageFieldDefault,
      isDisabled: ({ product }) =>
        !_.includes(
          [stagesCase.STAGE_LAWSUIT_COMPLETED, stagesCase.STAGE_TRIAL, stagesCase.STAGE_LAWSUIT],
          product.stage
        ),
      fieldType: "percentage",
      numberField: "float",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.lawsuitCompletedReason`,
      isDisabled: () => true,
      options: () => [
        { value: "courtDecision" },
        { value: "settlement" },
        { value: "lawsuitRevocation" },
        { value: "lawsuitRevocationBeforeHearing" },
        { value: "resolution" },
        { value: "defaultJudgement" },
        { value: "judgementAppeal" },
        { value: "judgementByConfession" },
      ],
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.lawsuitCompletedDate`,
      isHidden: ({ product, values }) =>
        !values[product.productClassName].lawsuitCompletedReason,
      validators: dateFieldDefault,
    },
  ];

  const lawsuitFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfClaimSentDate`,
      isHidden: ({ product }) => product.caseCategory !== "enforceClaim",
      isDisabled: () => true,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.paymentOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => Boolean(product.statementOfClaimFiledDate || product.caseCategory !== "defendClaim"),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.enforcementOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => Boolean(product.statementOfClaimFiledDate || product.caseCategory !== "defendClaim"),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfClaimFiledDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.advisoryHandling === true || product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courtReference`,
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfDefenseFiledDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(product.lawsuitCompletedReason),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.replicaSentDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        Boolean(
        product.caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(product.lawsuitCompletedReason)),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.statementOfDefenseSentDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.replicaFiledDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) =>
        product.caseCategory !== "defendClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(product.lawsuitCompletedReason),
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.judgementReceivedDate`,
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.lawsuitCompletedReason`,
      options: ({ product }) => {
        const options = [
          { value: "courtDecision" },
          { value: "settlement" },
          { value: "lawsuitRevocation" },
          { value: "lawsuitRevocationBeforeHearing" },
          { value: "resolution" },
          { value: "defaultJudgement" },
          { value: "judgementAppeal" },
          { value: "judgementByConfession" },
        ];

        if (product.isHigherInstanceCase) {
          options.push({ value: "courtOrder" });
        }

        return options;
      },
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.customerLawsuitCompletedApprovalDate`,
      translationPath: ({ values }) =>
        `${product.productClassName}.customerLawsuitCompletedApprovalDate.${
          values[product.productClassName].lawsuitCompletedReason
        }`,
      validators: dateFieldDefault,
      isHidden: ({ product, values }) =>
        _.includes(
          ["courtDecision", "defaultJudgement", "judgementAppeal", "judgementByConfession", "courtOrder"],
          values[product.productClassName].lawsuitCompletedReason
        ) || !values[product.productClassName].lawsuitCompletedReason,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.lawsuitCompletedDate`,
      isHidden: ({ product, values }) =>
        !values[product.productClassName].lawsuitCompletedReason,
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasStandardInvoicing`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasStandardInvoicing`),
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.netRvgAmount`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].hasStandardInvoicing,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialPreliminary`,
      fieldType: "money",
      numberType: "float",
      isDisabled: () => true,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialFinal`,
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasExtrajudicialValueInDisputeOverwrite`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasExtrajudicialValueInDisputeOverwrite`),
      isDisabled: ({ product }) =>
        !product.valueInDisputeJudicialFinal ||
        product.valueInDisputeJudicialFinal ===
        product.valueInDisputeExtrajudicialFinal,
      dependentValidationFields: [
        `${product.productClassName}.valueInDisputeExtrajudicialFinal`,
        `${product.productClassName}.valueInDisputeJudicialFinal`,
      ]
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.costBearingByOpponentRelative`,
      validators: percentageFieldDefault,
      isDisabled: ({ product }) =>
        !_.includes(
          [stagesCase.STAGE_LAWSUIT_COMPLETED, stagesCase.STAGE_TRIAL, stagesCase.STAGE_LAWSUIT],
          product.stage
        ),
      isHidden: ({ product }) =>
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(product.lawsuitCompletedReason),
      fieldType: "percentage",
      numberField: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.principalClaim`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          values[product.productClassName].lawsuitCompletedReason
        ) ||
        values[product.productClassName].costBearingByOpponentRelative === "0",
      numberType: "float",
      fieldType: "money",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.principalClaimDefaultDate`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          values[product.productClassName].lawsuitCompletedReason
        ) ||
        (values[product.productClassName].costBearingByOpponentRelative === "0" &&
          product.productClassName !== "employmentContract"),
      validators: dateFieldDefault,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.accessoryClaim`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          values[product.productClassName].lawsuitCompletedReason
        ) ||
        (values[product.productClassName].costBearingByOpponentRelative === "0" &&
          product.productClassName !== "employmentContract"),
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.accessoryClaimDefaultDate`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          values[product.productClassName].lawsuitCompletedReason
        ) ||
        (values[product.productClassName].costBearingByOpponentRelative === "0" &&
          product.productClassName !== "employmentContract"),
      validators: dateFieldDefault,
    },
  ];

  const lawsuitCompletedFields: FormElementPropsWithoutProduct<LitigationUnion>[] = [
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courtReference`,
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasStandardInvoicing`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasStandardInvoicing`),
      isDisabled: () => true,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.subtractExtrajudicialProcessFee`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.subtractExtrajudicialProcessFee`),
      isDisabled: () => true,
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.netRvgAmount`,
      isDisabled: () => true,
      isHidden: ({ product }) => Boolean(product.hasStandardInvoicing),
      fieldType: "money",
      numberType: "float",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.valueInDisputeJudicialFinal`,
      fieldType: "money",
      numberType: "float",
      isDisabled: () => true,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasExtrajudicialValueInDisputeOverwrite`,
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasExtrajudicialValueInDisputeOverwrite`),
      isDisabled: ({ product }) =>
        !product.valueInDisputeJudicialFinal ||
        product.valueInDisputeJudicialFinal ===
        product.valueInDisputeExtrajudicialFinal,
      dependentValidationFields: [
        `${product.productClassName}.valueInDisputeExtrajudicialFinal`,
        `${product.productClassName}.valueInDisputeJudicialFinal`,
      ]
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentRelative`,
      validators: percentageFieldDefault,
      isDisabled: () => true,
      isHidden: ({ product }) =>
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(product.lawsuitCompletedReason),
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.costBearingByOpponentFinalAbsolute`,
      helperText:
        "Bitte hier die vom Gegner auf das gerichtliche Verfahren tatsächlich gezahlten Kosten inkl. MwSt. eintragen. " +
        "Außergerichtliche Gebühren sind nicht zu berücksichtigen.",
      validators: [...invoiceNetValueNotOverpaid(`${product.productClassName}.netRvgAmount`)],
      isDisabled: ({ product }) => !product.lawsuitCompletedDate,
      isHidden: ({ product }) => !product.caseWasInLitigation,
      numberType: "float",
      fieldType: "money",
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.courtCostsFinal`,
      isDisabled: ({ product }) => !product.lawsuitCompletedDate,
      isHidden: ({ product }) =>
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing", "courtOrder"].includes(
          product.lawsuitCompletedReason
        ) ||
        product.costBearingByOpponentRelative === 0.0 ||
        product.costBearingByOpponentRelative === 100.0,
      numberType: "float",
      fieldType: "money",
    },
  ];

  const completedFields: FormElementPropsWithoutProduct[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategoryAssessment`,
      isDisabled: ({ product }) => product.stage! > stagesCase.STAGE_WAITING_FOR_FEEDBACK,
      isHidden: ({ product }) => product.caseCategory === "advisory",
      options: () => [{ value: "confirm" }, { value: "reject" }],
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.assessmentRejectionText`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment !== "reject" || product.caseCategory === "advisory",
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
        rows: 12,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.finalClaimStatusClient`,
      translationPath: `${product.productClassName}.finalClaimStatusClient.${product.caseCategory}`,
      isDisabled: () => true,
      isHidden: ({ product }) =>
        product.advisoryHandling === true || product.productClassName === "trafficAccident",
      options: () => [
        { value: "fullyAgreed" },
        { value: "partiallyAgreed" },
        { value: "notAgreed" },
        { value: "noReaction" },
      ],
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.finalProcessFeedbackClient`,
      translationPath: `${product.productClassName}.finalProcessFeedbackClient.${product.caseCategory}`,
      isDisabled: () => true,
      isHidden: ({ product }) => product.advisoryHandling === true,
      options: () => [{ value: "agreement" }, { value: "lawsuit" }],
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.customerLawsuitCompletedApprovalDate`,
      translationPath: ({ values }) =>
        `${product.productClassName}.customerLawsuitCompletedApprovalDate.${
          values[product.productClassName].lawsuitCompletedReason
        }`,
      isDisabled: () => true,
      isHidden: ({ values }) =>
        _.includes(
          ["courtDecision", "defaultJudgement", "judgementAppeal", "judgementByConfession"],
          values[product.productClassName].lawsuitCompletedReason
        ) || !values[product.productClassName].lawsuitCompletedReason,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.lawsuitCompletedDate`,
      isDisabled: () => true,
      isHidden: ({ product }) => product.advisoryHandling === true,
    },
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields,
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields,
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields,
    [stagesCase.STAGE_CHECKED]: checkedFields,
    [stagesCase.STAGE_IN_PROGRESS]: inProgressFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: communicationSentFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: reactionReceivedFields,
    [stagesCase.STAGE_ACTION_REQUIRED]: actionRequiredFields,
    [stagesCase.STAGE_PREPARE_LAWSUIT]: prepareLawsuitFields,
    [stagesCase.STAGE_LAWSUIT]: lawsuitFields,
    [stagesCase.STAGE_TRIAL]: lawsuitFields,
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]: evaluateJudgementFields,
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: lawsuitCompletedFields,
    [stagesCase.STAGE_COMPLETED]: completedFields,
  };
};
